import { apiClient } from '@/shared/api';
import type { Employee, EmployeePermissions, PendingEmployee } from '../types';

const BASE_PATH = '/v1/admin/organisations';

export async function getOrganisationEmployees(orgId: string): Promise<Employee[]> {
  const response = await apiClient.get<{ data?: { employees: Employee[] }; employees?: Employee[] }>(`${BASE_PATH}/${orgId}/employees`);
  return response.data.data?.employees || response.data.employees || [];
}

export async function addOrganisationEmployee(
  orgId: string,
  data: { userId: string; information?: { first_name?: string; last_name?: string }; notify?: boolean }
): Promise<Employee> {
  const response = await apiClient.post<Employee>(`${BASE_PATH}/${orgId}/employees`, data);
  return response.data;
}

export async function removeOrganisationEmployee(orgId: string, employeeId: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${orgId}/employees/${employeeId}`);
}

export async function updateEmployeePermissions(
  orgId: string,
  userId: string,
  permissions: EmployeePermissions
): Promise<void> {
  await apiClient.patch(`${BASE_PATH}/${orgId}/employees/${userId}`, { permissions });
}

// Pending Employees (Invites)
export async function getPendingEmployees(orgId: string): Promise<PendingEmployee[]> {
  const response = await apiClient.get<{ data?: { pending: PendingEmployee[] }; pending?: PendingEmployee[] }>(
    `${BASE_PATH}/${orgId}/employees/pending`
  );
  return response.data.data?.pending || response.data.pending || [];
}

export async function inviteEmployee(
  orgId: string,
  data: {
    email: string;
    first_name: string;
    last_name: string;
    can_invite?: boolean;
  },
  loginUrl: string
): Promise<void> {
  const payload = {
    email_parameters: {
      web_url: loginUrl,
    },
    invites: [
      {
        information: {
          first_name: data.first_name.trim(),
          last_name: data.last_name.trim(),
        },
        email: data.email.trim(),
        role_id: '88c0a3ca-cbce-4b27-b390-f6a001ca129f',
        ...(data.can_invite && {
          permissions: {
            access: true,
            modules: {
              registration: {
                can: ['view', 'manage'],
                licensors: {},
                name: 'registration',
              },
            },
            system: {
              users: {
                can: ['invite', 'view', 'edit', 'delete'],
              },
            },
          },
        }),
      },
    ],
  };

  await apiClient.post(`/v1/admin/admin/organisations/${orgId}/employees`, payload);
}

export async function resendEmployeeInvite(
  orgId: string,
  inviteToken: string,
  userId: string,
  loginUrl: string
): Promise<void> {
  await apiClient.post(`/v1/admin/admin/organisations/${orgId}/employees/${inviteToken}`, {
    user_id: userId,
    web_url: loginUrl,
  });
}

export async function deleteEmployeeInvite(orgId: string, inviteToken: string): Promise<void> {
  await apiClient.delete(`/v1/admin/admin/organisations/${orgId}/employees/${inviteToken}`);
}

export async function triggerOrgNotification(params: {
  organisationId: string;
  emails: string[];
  notification: string;
}): Promise<void> {
  await apiClient.post('/v1/admin/trigger-notification', params);
}
